import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useLang } from "../i18n";
import api, { formatApiErrorDetail } from "../lib/api";
import { Reveal } from "./motion";
import { LeafSprig } from "./Botanicals";
import SocialIcon from "./SocialIcon";
import { SOCIALS } from "../lib/social";

const Footer = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success(t("contact.sent"));
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <footer id="contact" data-testid="site-footer" className="relative overflow-hidden bg-primary text-primary-foreground">
      <LeafSprig className="pointer-events-none absolute bottom-4 left-2 w-24 text-primary-foreground/10 animate-sway hidden md:block" />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-24 pb-12">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
          <Reveal>
            <p className="text-secondary text-sm uppercase tracking-[0.25em] mb-6">{t("contact.title")}</p>
            <h2 className="font-serif text-5xl md:text-7xl leading-[0.95] tracking-tightest mb-8">
              {t("footer.cta")}
            </h2>
            <p className="text-primary-foreground/70 max-w-md text-lg font-light">{t("contact.sub")}</p>
          </Reveal>

          <Reveal delay={0.1}>
            <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
              <input
                data-testid="contact-name"
                required
                placeholder={t("contact.name")}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-transparent border-b border-primary-foreground/30 py-3 text-primary-foreground placeholder:text-primary-foreground/50 focus:border-secondary outline-none transition-colors"
              />
              <input
                data-testid="contact-email"
                type="email"
                required
                placeholder={t("contact.email")}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full bg-transparent border-b border-primary-foreground/30 py-3 text-primary-foreground placeholder:text-primary-foreground/50 focus:border-secondary outline-none transition-colors"
              />
              <textarea
                data-testid="contact-message"
                required
                rows={3}
                placeholder={t("contact.message")}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full bg-transparent border-b border-primary-foreground/30 py-3 text-primary-foreground placeholder:text-primary-foreground/50 focus:border-secondary outline-none transition-colors resize-none"
              />
              <button
                data-testid="contact-submit"
                disabled={sending}
                className="bg-secondary text-secondary-foreground px-8 py-3.5 text-sm font-medium hover:bg-secondary/90 active:scale-95 transition-[transform,background-color] duration-200 disabled:opacity-60"
              >
                {sending ? "…" : t("contact.send")}
              </button>
            </form>
          </Reveal>
        </div>

        <div className="mt-24 pt-8 border-t border-primary-foreground/20 flex flex-col md:flex-row justify-between items-start md:items-center gap-6 text-sm text-primary-foreground/60">
          <div>
            <span className="font-serif text-3xl text-primary-foreground tracking-tightest">Nordicvy</span>
            <p className="mt-2">{t("footer.tagline")}</p>
            <div className="flex gap-3 mt-5">
              {SOCIALS.map((s) => (
                <a
                  key={s.name}
                  data-testid={`footer-social-${s.name}`}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className="w-9 h-9 flex items-center justify-center border border-primary-foreground/25 text-primary-foreground/80 hover:bg-secondary hover:text-secondary-foreground hover:border-secondary transition-colors"
                >
                  <SocialIcon name={s.name} className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>
          <div className="flex gap-8">
            <Link to="/shop" className="link-underline hover:text-primary-foreground">{t("nav.shop")}</Link>
            <Link to="/booking" className="link-underline hover:text-primary-foreground">{t("nav.booking")}</Link>
            <Link to="/members" className="link-underline hover:text-primary-foreground">{t("nav.members")}</Link>
            <Link to="/blog" className="link-underline hover:text-primary-foreground">{t("nav.blog")}</Link>
            <Link to="/conditions" className="link-underline hover:text-primary-foreground">{lang === "fr" ? "Conditions" : "Terms"}</Link>
          </div>
          <p>© {new Date().getFullYear()} Nordicvy. {t("footer.rights")}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
